<template>
  <div class="contact-page light-panel">
    <section class="contact-letter reveal">
      <div class="container container-medium">
        <div class="contact-review">
          <div class="contact-review__quote-mark" aria-hidden="true"></div>
          <div class="contact-review__layout">
            <div class="contact-review__photo">
              <img :src="page.ceoImage" alt="SHIRLEY, CEO">
            </div>
            <div class="contact-review__body">
              <blockquote>
                <p v-for="paragraph in page.letter" :key="paragraph">{{ paragraph }}</p>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="message" ref="message" class="contact-form-panel reveal">
      <div class="container container-narrow">
        <ul class="contact-form-tabs">
          <li>
            <button
              type="button"
              class="contact-form-tabs__btn"
              :class="{ 'is-active': formStep === 1 }"
              @click="formStep = 1"
            >
              {{ $t('common.message') }}
            </button>
          </li>
          
        </ul>

        <form class="contact-form" @submit.prevent="handleSubmit">
          <div v-show="formStep === 1" class="contact-form__step">
            <div class="contact-form__row">
              <label>
                <span>{{ $t('contact.iAm') }} <em>*</em></span>
                <select v-model="form.customerType" required>
                  <option disabled value="">{{ $t('common.pleaseSelect') }}</option>
                  <option
                    v-for="item in page.customerTypes"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </label>
              <label>
                <span>{{ $t('contact.typeOfQuestion') }} <em>*</em></span>
                <select v-model="form.requestType" required>
                  <option disabled value="">{{ $t('common.pleaseSelect') }}</option>
                  <option
                    v-for="item in page.requestTypes"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </label>
            </div>

            <fieldset v-if="form.requestType === 'handout'" class="contact-form__handouts">
              <legend class="visually-hidden">{{ $t('contact.informationMaterial') }}</legend>
              <label
                v-for="item in page.handoutTypes"
                :key="item.value"
                class="contact-form__check"
              >
                <input v-model="form.handouts" type="checkbox" :value="item.value">
                <span>{{ item.label }}</span>
              </label>
            </fieldset>

            <div class="contact-form__row">
              <label>
                <span>{{ $t('contact.salutation') }} <em>*</em></span>
                <select v-model="form.salutation" required>
                  <option disabled value="">{{ $t('common.pleaseSelect') }}</option>
                  <option
                    v-for="item in page.salutations"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </label>
              <label>
                <span>{{ $t('common.firstName') }} <em>*</em></span>
                <input v-model.trim="form.firstName" type="text" required>
              </label>
            </div>

            <div class="contact-form__row">
              <label>
                <span>{{ $t('common.lastName') }} <em>*</em></span>
                <input v-model.trim="form.lastName" type="text" required>
              </label>
              <label>
                <span>{{ $t('common.emailAddress') }} <em>*</em></span>
                <input v-model.trim="form.email" type="email" required>
              </label>
            </div>

            <label>
              <span>{{ $t('common.messageToUs') }}</span>
              <textarea v-model.trim="form.message" rows="6"></textarea>
            </label>

            <button type="button" class="contact-form__continue" @click="goToContactData">
              {{ $t('common.next') }}
            </button>
          </div>

          <div v-show="formStep === 2" class="contact-form__step">
            <div class="contact-form__row">
              <label>
                <span>{{ $t('common.country') }} <em>*</em></span>
                <select v-model="form.country" required>
                  <option disabled value="">{{ $t('common.pleaseSelect') }}</option>
                  <option v-for="country in page.countries" :key="country" :value="country">
                    {{ country }}
                  </option>
                </select>
              </label>
              <label>
                <span>{{ $t('common.zipCode') }} <em>*</em></span>
                <input v-model.trim="form.zip" type="text" required>
              </label>
            </div>
            <div class="contact-form__row">
              <label>
                <span>{{ $t('common.phone') }} <em>*</em></span>
                <input v-model.trim="form.phone" type="text" required>
              </label>
              <label>
                <span>{{ $t('common.company') }}</span>
                <input v-model.trim="form.company" type="text">
              </label>
            </div>
            <button type="submit" class="contact-form__submit" :disabled="submitting">{{ submitting ? $t('common.submitting') : $t('common.submit') }}</button>
            <p v-if="submitMessage" class="contact-form__feedback">{{ submitMessage }}</p>
          </div>
        </form>
      </div>
    </section>

    <section id="factory" ref="factory" class="contact-factory reveal">
      <div class="container">
        <h2 class="contact-section-title contact-section-title--light">{{ site.factory.title }}</h2>
        <div class="contact-factory__layout">
          <div class="contact-factory__map">
            <img :src="page.factoryMapImage" alt="" loading="lazy">
          </div>
          <div class="contact-factory__list">
            <article
              v-for="location in site.factory.locations"
              :key="location.title"
              class="contact-factory__item"
            >
              <h4>{{ location.title }}</h4>
              <p><strong>{{ $t('common.country') }}:</strong> {{ location.country }}</p>
              <p><strong>{{ $t('common.address') }}:</strong> {{ location.address }}</p>
              <p><strong>{{ $t('common.phone') }}:</strong> {{ location.phone }}</p>
              <p v-if="location.whatsapp"><strong>{{ $t('common.whatsapp') }}:</strong> {{ location.whatsapp }}</p>
              <p>
                <strong>{{ $t('common.email') }}:</strong>
                <a :href="'mailto:' + location.email">{{ location.email }}</a>
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section id="partner" ref="partner" class="contact-partner reveal">
      <div class="container">
        <div class="contact-partner__layout">
          <div class="contact-partner__copy">
            <p class="contact-partner__eyebrow">{{ page.partnerEyebrow }}</p>
            <h2 class="contact-section-title contact-section-title--light">{{ page.partnerTitle }}</h2>
            <p v-for="paragraph in page.partnerCopy" :key="paragraph">{{ paragraph }}</p>
          </div>
          <div class="contact-partner__media">
            <img :src="page.partnerImage" alt="Distribution partner" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="contact-projects reveal">
      <div class="container">
        <p class="contact-projects__eyebrow">{{ page.projectsEyebrow }}</p>
        <h2 class="contact-section-title">{{ page.projectsTitle }}</h2>
        <div class="contact-projects__gallery">
          <div class="contact-mosaic contact-mosaic--a">
            <button
              v-for="(image, index) in page.projectsGalleryRow1"
              :key="'contact-mosaic-a-' + index"
              type="button"
              class="contact-mosaic__cell contact-mosaic__cell--lightbox"
              :aria-label="$t('contact.previewProjectImage') + ' ' + (index + 1)"
              @click="openProjectLightbox(index)"
            >
              <span class="contact-mosaic__media">
                <img :src="image" alt="" loading="lazy">
              </span>
              <span class="contact-mosaic__corner" aria-hidden="true"></span>
            </button>
          </div>
          <div class="contact-mosaic contact-mosaic--b">
            <button
              v-for="(image, index) in page.projectsGalleryRow2"
              :key="'contact-mosaic-b-' + index"
              type="button"
              class="contact-mosaic__cell contact-mosaic__cell--lightbox"
              :aria-label="$t('contact.previewProjectImage') + ' ' + (index + 6)"
              @click="openProjectLightbox(index + 5)"
            >
              <span class="contact-mosaic__media">
                <img :src="image" alt="" loading="lazy">
              </span>
              <span class="contact-mosaic__corner" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <transition name="fade">
      <div
        v-if="projectLightboxOpen"
        class="contact-lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="`Project image ${projectLightboxIndex + 1} of ${projectGallery.length}`"
        @click.self="closeProjectLightbox"
      >
        <button type="button" class="contact-lightbox__close" aria-label="Close" @click="closeProjectLightbox">×</button>
        <button type="button" class="contact-lightbox__nav contact-lightbox__nav--prev" aria-label="Previous" @click="stepProjectLightbox(-1)">‹</button>
        <img :src="projectGallery[projectLightboxIndex]" alt="">
        <button type="button" class="contact-lightbox__nav contact-lightbox__nav--next" aria-label="Next" @click="stepProjectLightbox(1)">›</button>
      </div>
    </transition>
  </div>
</template>

<script>
import site from '../mock/strongova'
import { fetchPublicPageSections, resolveAssetUrl, submitInquiry } from '../api/public'

const SECTION_MAP = {
  quote: 'message',
  '3': 'message',
  factory: 'factory',
  '1': 'factory',
  partner: 'partner',
  '2': 'partner'
}

export default {
  name: 'ContactPage',
  data () {
    return {
      site,
      page: JSON.parse(JSON.stringify(site.contact)),
      formStep: 1,
      submitMessage: '',
      submitting: false,
      projectLightboxOpen: false,
      projectLightboxIndex: 0,
      form: {
        customerType: '',
        requestType: '',
        handouts: [],
        salutation: '',
        firstName: '',
        lastName: '',
        email: '',
        message: '',
        country: '',
        zip: '',
        phone: '',
        company: ''
      }
    }
  },
  computed: {
    projectGallery () {
      return [
        ...this.page.projectsGalleryRow1,
        ...this.page.projectsGalleryRow2
      ]
    }
  },
  mounted () {
    this.loadPageSections()
    this.$nextTick(this.scrollToSection)
    window.addEventListener('keydown', this.onProjectLightboxKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onProjectLightboxKeydown)
    document.body.style.overflow = ''
  },
  watch: {
    '$route.fullPath' () {
      this.$nextTick(this.scrollToSection)
    }
  },
  methods: {
    loadPageSections () {
      fetchPublicPageSections({ pageKey: 'contact' })
        .then(sections => {
          if (Array.isArray(sections) && sections.length) {
            this.applyPageSections(sections)
          }
        })
        .catch(() => {
          this.page = JSON.parse(JSON.stringify(site.contact))
        })
    },
    applyPageSections (sections) {
      const page = JSON.parse(JSON.stringify(site.contact))
      const intro = sections.find(item => item.sectionKey === 'intro')
      const factory = sections.find(item => item.sectionKey === 'factory')
      const partner = sections.find(item => item.sectionKey === 'contact')
      const projects = sections.find(item => item.sectionKey === 'gallery')

      if (intro) {
        page.ceoName = intro.title || page.ceoName
        page.ceoImage = resolveAssetUrl(intro.image, page.ceoImage)
        if (intro.description) {
          page.letter = intro.description
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
        }
      }

      if (factory) {
        page.factoryMapImage = resolveAssetUrl(factory.image, page.factoryMapImage)
      }

      if (partner) {
        page.partnerTitle = partner.title || page.partnerTitle
        page.partnerImage = resolveAssetUrl(partner.image, page.partnerImage)
        if (partner.description) {
          const lines = partner.description
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
          if (lines.length) {
            page.partnerEyebrow = lines[0]
            page.partnerCopy = lines.slice(1)
          }
        }
      }

      if (projects) {
        page.projectsTitle = projects.title || page.projectsTitle
        page.projectsEyebrow = projects.description || page.projectsEyebrow
        if (Array.isArray(projects.imagesJson) && projects.imagesJson.length) {
          const images = projects.imagesJson.map(item => resolveAssetUrl(item.image)).filter(Boolean)
          page.projectsGalleryRow1 = images.slice(0, Math.ceil(images.length / 2))
          page.projectsGalleryRow2 = images.slice(Math.ceil(images.length / 2))
        }
      }

      this.page = page
      this.projectLightboxIndex = 0
      this.$nextTick(this.scrollToSection)
    },
    resolveSection () {
      const tab = this.$route.query.tab
      if (tab && SECTION_MAP[tab]) return SECTION_MAP[tab]

      const keys = Object.keys(this.$route.query)
      for (const key of keys) {
        if (SECTION_MAP[key]) return SECTION_MAP[key]
      }

      const match = this.$route.fullPath.match(/contact(?:\.html)?\?([123])(?:$|&)/)
      if (match) return SECTION_MAP[match[1]]

      return 'message'
    },
    scrollToSection () {
      const sectionId = this.resolveSection()
      const target = this.$refs[sectionId]
      if (!target) return
      const top = target.getBoundingClientRect().top + window.pageYOffset - 96
      window.scrollTo({ top, behavior: 'smooth' })
    },
    goToContactData () {
      this.formStep = 2
    },
    handleSubmit () {
      if (this.submitting) return
      this.submitting = true
      this.submitMessage = ''

      submitInquiry({
        source: 'contact',
        ...this.form
      })
        .then(() => {
          this.submitMessage = `Thank you, ${this.form.firstName || 'friend'}. Your message has been submitted.`
        })
        .catch(() => {
          this.submitMessage = 'Sorry, the message could not be submitted. Please try again later.'
        })
        .then(() => {
          this.submitting = false
        })
    },
    openProjectLightbox (index) {
      this.projectLightboxIndex = index
      this.projectLightboxOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeProjectLightbox () {
      this.projectLightboxOpen = false
      document.body.style.overflow = ''
    },
    stepProjectLightbox (delta) {
      const total = this.projectGallery.length
      this.projectLightboxIndex = (this.projectLightboxIndex + delta + total) % total
    },
    onProjectLightboxKeydown (event) {
      if (!this.projectLightboxOpen) return
      if (event.key === 'Escape') this.closeProjectLightbox()
      if (event.key === 'ArrowLeft') this.stepProjectLightbox(-1)
      if (event.key === 'ArrowRight') this.stepProjectLightbox(1)
    }
  }
}
</script>

<style>
.contact-page {
  color: #171411;
  background: #f6f1e9;
}

.container-medium {
  width: min(1080px, calc(100vw - 48px));
  margin: 0 auto;
}

.container-narrow {
  width: min(920px, calc(100vw - 48px));
  margin: 0 auto;
}

.contact-section-title {
  margin: 0 0 36px;
  font-size: clamp(30px, 2.8vw, 44px);
  font-weight: 400;
  line-height: 1.08;
}

.contact-section-title--light {
  color: #f5f3ef;
}

/* —— Letter —— */
.contact-letter {
  padding: clamp(88px, 10vw, 120px) 0 clamp(72px, 8vw, 96px);
}

.contact-review {
  position: relative;
  padding: 40px 32px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.34);
  box-shadow: 0 20px 40px rgba(23, 20, 17, 0.06);
}

.contact-review__quote-mark {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.contact-review__quote-mark::before,
.contact-review__quote-mark::after {
  position: absolute;
  width: 13px;
  height: 13px;
  content: "";
}

.contact-review__quote-mark::before {
  top: 0;
  right: 0;
  border-top: 2px solid #d0ccc4;
  border-right: 2px solid #d0ccc4;
}

.contact-review__quote-mark::after {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid #d0ccc4;
  border-left: 2px solid #d0ccc4;
}

.contact-review__layout {
  display: grid;
  grid-template-columns: minmax(200px, 260px) minmax(0, 1fr);
  gap: clamp(40px, 5vw, 80px);
  align-items: center;
}

.contact-review__photo {
  aspect-ratio: 1;
  overflow: hidden;
  background: #e8e2d9;
  border-radius: 12px;
}

.contact-review__photo img,
.contact-factory__map img,
.contact-partner__media img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.contact-review__body blockquote {
  margin: 0;
}

.contact-review__body blockquote p:last-child {
  margin-bottom: 0;
}

.contact-review__body p {
  margin: 0 0 18px;
  line-height: 1.8;
  font-size: clamp(16px, 1.45vw, 18px);
  color: rgba(23, 20, 17, 0.88);
}

.contact-review__author {
  margin: 32px 0 10px;
  font-size: 13px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.72);
}

.contact-review__email {
  color: #171411;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.contact-review__email:hover {
  color: #5a4a38;
}

/* —— Form —— */
.contact-form-panel {
  padding: clamp(64px, 8vw, 88px) 0;
  background: linear-gradient(180deg, #ddd7cd 0%, #d4cdc2 100%);
}

.contact-form-tabs {
  display: flex;
  justify-content: center;
  margin: 0 auto 44px;
  padding: 0;
  list-style: none;
}

.contact-form-tabs__btn {
  min-width: 160px;
  min-height: 50px;
  padding: 12px 28px;
  border: 1px solid rgba(23, 20, 17, 0.28);
  margin-left: -1px;
  background: transparent;
  color: #171411;
  font-size: 15px;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.contact-form-tabs li:first-child .contact-form-tabs__btn {
  margin-left: 0;
}

.contact-form-tabs__btn.is-active {
  background: #171411;
  color: #f5f3ef;
  border-color: #171411;
  position: relative;
  z-index: 1;
}

.contact-form-tabs__btn.is-disabled:disabled {
  opacity: 0.4;
  cursor: default;
}

.contact-form {
  display: grid;
  gap: 20px;
  padding: 34px;
  border: 1px solid rgba(23, 20, 17, 0.08);
  border-radius: 12px;
  background: rgba(246, 241, 233, 0.56);
  box-shadow: 0 20px 44px rgba(23, 20, 17, 0.08);
}

.contact-form label span {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(23, 20, 17, 0.62);
  letter-spacing: 0.02em;
}

.contact-form em {
  font-style: normal;
}

.contact-form input,
.contact-form select,
.contact-form textarea {
  width: 100%;
  padding: 15px 16px;
  border: 1px solid rgba(23, 20, 17, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.54);
  color: #171411;
  font-size: 15px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.contact-form input:focus,
.contact-form select:focus,
.contact-form textarea:focus {
  outline: none;
  border-color: rgba(183, 139, 95, 0.82);
  background: #f8f4ee;
}

.contact-form__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.contact-form__handouts {
  margin: 4px 0 0;
  padding: 20px 22px;
  border: 1px solid rgba(23, 20, 17, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.32);
  display: grid;
  gap: 12px;
}

.contact-form__check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 15px;
  line-height: 1.45;
}

.contact-form__check input {
  width: auto;
  margin-top: 3px;
  flex-shrink: 0;
}

.contact-form__continue,
.contact-form__submit {
  justify-self: start;
  min-width: 200px;
  min-height: 56px;
  margin-top: 8px;
  padding: 0 32px;
  border: 0;
  background: #171411;
  color: #f5f3ef;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(23, 20, 17, 0.16);
  transition: background 0.2s ease, transform 0.2s ease;
}

.contact-form__continue:hover,
.contact-form__submit:hover {
  background: #2a2622;
}

.contact-form__feedback {
  margin: 12px 0 0;
  color: rgba(23, 20, 17, 0.72);
}

/* —— Factory (dark) —— */
.contact-factory {
  padding: clamp(80px, 9vw, 104px) 0;
  background: #171411;
  color: #f5f3ef;
}

.contact-factory__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(300px, 0.88fr);
  gap: clamp(40px, 5vw, 72px);
  align-items: center;
}

.contact-factory__map {
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 12px;
}

.contact-factory__map::before {
  position: absolute;
  inset: 18px 18px -18px -18px;
  border: 1px solid rgba(245, 243, 239, 0.22);
  content: "";
  pointer-events: none;
  z-index: 1;
}

.contact-factory__map img {
  position: relative;
  z-index: 0;
}

.contact-factory__item {
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(245, 243, 239, 0.14);
}

.contact-factory__item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.contact-factory__item + .contact-factory__item {
  margin-top: 28px;
}

.contact-factory__item h4 {
  margin: 0 0 14px;
  font-size: clamp(18px, 1.6vw, 22px);
  font-weight: 400;
  color: #f5f3ef;
}

.contact-factory__item p {
  margin: 0 0 8px;
  line-height: 1.75;
  font-size: 15px;
  color: rgba(245, 243, 239, 0.78);
}

.contact-factory__item strong {
  color: rgba(245, 243, 239, 0.95);
  font-weight: 500;
}

.contact-factory__item a {
  color: #f5f3ef;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.contact-factory__item a:hover {
  color: rgba(245, 243, 239, 0.72);
}

/* —— Partner (dark) —— */
.contact-partner {
  padding: clamp(80px, 9vw, 104px) 0;
  background: #121110;
  color: #f5f3ef;
  border-top: 1px solid rgba(245, 243, 239, 0.08);
}

.contact-partner__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(280px, 0.92fr);
  gap: clamp(40px, 5vw, 72px);
  align-items: center;
}

.contact-partner__eyebrow {
  margin: 0 0 14px;
  font-size: 14px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(245, 243, 239, 0.58);
}

.contact-partner__copy p {
  margin: 0 0 18px;
  line-height: 1.85;
  font-size: 15px;
  color: rgba(245, 243, 239, 0.8);
}

.contact-partner__copy p:last-child {
  margin-bottom: 0;
}

.contact-partner__media {
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #2a2622;
  border-radius: 12px;
}

/* —— Projects —— */
.contact-projects {
  padding: clamp(80px, 9vw, 104px) 0 clamp(96px, 10vw, 120px);
  background: #f6f1e9;
}

.contact-projects__eyebrow {
  margin: 0 0 14px;
  font-size: 14px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.55);
}

.contact-projects__gallery {
  margin-top: clamp(28px, 4vw, 40px);
}

.contact-mosaic {
  display: grid;
  gap: 10px;
}

.contact-mosaic + .contact-mosaic {
  margin-top: 10px;
}

.contact-mosaic--a {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    "s1 s2"
    "s3 s4"
    "big big";
}

.contact-mosaic--a .contact-mosaic__cell:nth-child(1) { grid-area: s1; }
.contact-mosaic--a .contact-mosaic__cell:nth-child(2) { grid-area: s2; }
.contact-mosaic--a .contact-mosaic__cell:nth-child(3) { grid-area: s3; }
.contact-mosaic--a .contact-mosaic__cell:nth-child(4) { grid-area: s4; }
.contact-mosaic--a .contact-mosaic__cell:nth-child(5) { grid-area: big; }

.contact-mosaic--b {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    "big big"
    "s1 s2"
    "s3 s4";
}

.contact-mosaic--b .contact-mosaic__cell:nth-child(1) { grid-area: big; }
.contact-mosaic--b .contact-mosaic__cell:nth-child(2) { grid-area: s1; }
.contact-mosaic--b .contact-mosaic__cell:nth-child(3) { grid-area: s2; }
.contact-mosaic--b .contact-mosaic__cell:nth-child(4) { grid-area: s3; }
.contact-mosaic--b .contact-mosaic__cell:nth-child(5) { grid-area: s4; }

.contact-mosaic__cell {
  position: relative;
  overflow: hidden;
  min-height: 0;
  background: #e8e2d9;
  border-radius: 0;
}

.contact-mosaic__cell--lightbox {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  text-align: left;
  cursor: zoom-in;
  transition: opacity 0.2s ease;
}

.contact-mosaic__cell--lightbox:hover {
  opacity: 0.98;
}

.contact-mosaic__media {
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.contact-mosaic__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease-in-out;
}

.contact-mosaic__cell--lightbox:hover .contact-mosaic__media img {
  transform: scale(1.1);
}

.contact-mosaic__corner {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 35px;
  height: 35px;
  pointer-events: none;
}

.contact-mosaic__corner::before,
.contact-mosaic__corner::after {
  position: absolute;
  width: 13px;
  height: 13px;
  content: "";
}

.contact-mosaic__corner::before {
  top: 3px;
  right: 3px;
  border-top: 2px solid #e2e2e2;
  border-right: 2px solid #e2e2e2;
}

.contact-mosaic__corner::after {
  bottom: 3px;
  left: 3px;
  border-bottom: 2px solid #e2e2e2;
  border-left: 2px solid #e2e2e2;
}

.contact-lightbox {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 48px;
  background: rgba(0, 0, 0, 0.88);
}

.contact-lightbox img {
  max-width: min(1200px, 92vw);
  max-height: 82vh;
  object-fit: contain;
}

.contact-lightbox__close {
  position: absolute;
  top: 24px;
  right: 28px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 42px;
  line-height: 1;
  cursor: pointer;
}

.contact-lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
}

.contact-lightbox__nav--prev {
  left: 24px;
}

.contact-lightbox__nav--next {
  right: 24px;
}

.contact-mosaic--a .contact-mosaic__cell:nth-child(-n+4),
.contact-mosaic--b .contact-mosaic__cell:nth-child(n+2) {
  aspect-ratio: 1 / 1;
}

.contact-mosaic--a .contact-mosaic__cell:nth-child(5),
.contact-mosaic--b .contact-mosaic__cell:nth-child(1) {
  aspect-ratio: 2 / 1;
  min-height: 200px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (min-width: 1201px) {
  .contact-mosaic--a {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(180px, auto));
    grid-template-areas:
      "s1 s2 big big"
      "s3 s4 big big";
  }

  .contact-mosaic--b {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(180px, auto));
    grid-template-areas:
      "big big s1 s2"
      "big big s3 s4";
  }

  .contact-mosaic--a .contact-mosaic__cell:nth-child(-n+4),
  .contact-mosaic--b .contact-mosaic__cell:nth-child(n+2) {
    aspect-ratio: auto;
    min-height: 180px;
  }

  .contact-mosaic--a .contact-mosaic__cell:nth-child(5),
  .contact-mosaic--b .contact-mosaic__cell:nth-child(1) {
    aspect-ratio: auto;
    min-height: 100%;
  }
}

@media (max-width: 1200px) and (min-width: 769px) {
  .contact-mosaic--a,
  .contact-mosaic--b {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
  }

  .contact-mosaic--a {
    grid-template-areas:
      "s1 s2"
      "s3 s4"
      "big big";
  }

  .contact-mosaic--b {
    grid-template-areas:
      "big big"
      "s1 s2"
      "s3 s4";
  }
}

@media (max-width: 900px) {
  .contact-review__layout,
  .contact-factory__layout,
  .contact-partner__layout,
  .contact-form__row {
    grid-template-columns: 1fr;
  }

  .contact-review {
    padding: 28px 8px 0;
    background: transparent;
    box-shadow: none;
  }

  .contact-factory__map::before {
    inset: 12px 12px -12px -12px;
  }

  .contact-form-tabs {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .contact-form-tabs li {
    display: block;
  }

  .contact-form-tabs__btn {
    margin-left: 0;
    min-width: 140px;
  }

  .contact-form {
    padding: 24px 20px;
  }
}

@media (max-width: 640px) {
  .container-medium,
  .container-narrow {
    width: calc(100vw - 32px);
  }

  .contact-letter {
    padding: 76px 0 56px;
  }

  .contact-review {
    padding: 0;
  }

  .contact-review__layout {
    gap: 24px;
  }

  .contact-review__photo {
    max-width: 220px;
  }

  .contact-review__body p {
    font-size: 15px;
    line-height: 1.75;
  }

  .contact-form-panel,
  .contact-factory,
  .contact-partner,
  .contact-projects {
    padding: 56px 0;
  }

  .contact-section-title {
    margin-bottom: 24px;
    font-size: 30px;
  }

  .contact-form-tabs {
    margin-bottom: 24px;
  }

  .contact-form-tabs__btn,
  .contact-form__continue,
  .contact-form__submit {
    width: 100%;
  }

  .contact-form {
    padding: 22px 16px;
  }

  .contact-form__continue,
  .contact-form__submit {
    justify-self: stretch;
  }

  .contact-factory__layout,
  .contact-partner__layout {
    gap: 28px;
  }

  .contact-factory__map::before {
    display: none;
  }

  .contact-factory__item {
    padding-bottom: 22px;
  }

  .contact-factory__item + .contact-factory__item {
    margin-top: 22px;
  }

  .contact-mosaic,
  .contact-mosaic--a,
  .contact-mosaic--b {
    grid-template-columns: 1fr;
    grid-template-areas: none;
    gap: 8px;
  }

  .contact-mosaic--a .contact-mosaic__cell,
  .contact-mosaic--b .contact-mosaic__cell {
    grid-area: auto;
    aspect-ratio: 4 / 3;
    min-height: 0;
  }

  .contact-mosaic--a .contact-mosaic__cell:nth-child(5),
  .contact-mosaic--b .contact-mosaic__cell:nth-child(1) {
    aspect-ratio: 4 / 3;
    min-height: 0;
  }

  .contact-lightbox {
    padding: 70px 16px 24px;
  }

  .contact-lightbox__close {
    top: 18px;
    right: 16px;
  }

  .contact-lightbox__nav {
    top: auto;
    bottom: 22px;
    transform: none;
  }

  .contact-lightbox__nav--prev {
    left: 16px;
  }

  .contact-lightbox__nav--next {
    right: 16px;
  }
}
</style>
